<template>
  <Card
    :class="variant === SitterCardVariantTypes.Small && 'small-sitter-card'"
    :clickable="isClickable"
    :href="profileUrl"
    :customClasses="customClasses"
  >
    <AlertBanner v-if="availability" :status="validStatus">
      <template #icon>
        <CalendarIcon />
      </template>

      Available: {{ availabilityText }}
    </AlertBanner>
    <AlertBanner v-if="shouldShowAvailabilityAlert" class="-m-16 mb-16" :status="6">
      <div>You sent an availability check</div>
    </AlertBanner>
    <div v-if="isLoading"><SitterCardLoadingSkeleton /></div>
    <div v-else class="p-16">
      <div v-if="variant === SitterCardVariantTypes.Base" class="flex flex-col">
        <div class="w-full flex flex-row border-b-2 border-solid border-grey-divider-subdued pb-4 last:pb-0 last:border-b-0">
          <div class="w-full pr-16 overflow-hidden">
            <h2 class="text-h3 text-ellipsis overflow-hidden whitespace-nowrap">{{ name }}</h2>
            <p v-if="yearsExperience && yearsExperience !== 0" class="text-body">{{ yearsExperiencePluralized }}</p>
            <p class="text-body">{{ locationText }}</p>
            <StarRating v-if="totalRatings > 0" :count="totalRatings || 0" :average="avgRating" />
          </div>
          <div>
            <a v-if="photoUploadAvatar" class="relative" aria-label="Photo upload" href="/sitter/photo_upload#/sitter_upload">
              <Avatar photoUploadIcon size="mega" name="" emptyAlt />
              <div class="flex justify-center items-center w-32 h-32 absolute top-14 right-0" aria-label="Add profile photo">
                <AddFilled />
              </div>
            </a>
            <Avatar v-else :name="name" :image-url="photoUrl" size="mega" />
          </div>
        </div>
        <p v-if="truncatedDescription" id="sitter-card-description" class="text-small">{{ truncatedDescription }}</p>
        <div v-if="hasEnhancedBgc || hasBgc || hasMvr || hasFirstAid || hasCpr || hasRate" class="flex">
          <div v-if="hasIdv || hasEnhancedBgc || hasBgc || hasMvr || hasFirstAid || hasCpr">
            <ul class="mt-16 inline-flex">
              <li v-if="hasIdv">
                <IDVBadge />
              </li>
              <li v-if="hasEnhancedBgc" id="sitter-card-enhanced-bgc">
                <EnhancedBackgroundCheckBadge />
              </li>
              <li v-if="hasBgc" id="sitter-card-bgc" :class="hideBGCBadge ? 'hidden md:block' : ''">
                <BackgroundCheckBadge />
              </li>
              <li v-if="hasMvr" id="sitter-card-mvr">
                <MotorVehicleRecordBadgeVue />
              </li>
              <li v-if="hasFirstAid" id="sitter-card-first-aid">
                <FirstAidBadge />
              </li>
              <li v-if="hasCpr" id="sitter-card-cpr">
                <CPRBadge />
              </li>
            </ul>
          </div>
          <span v-if="hasRate" class="ml-auto self-center">
            <span class="profile-card__rate-unit">$</span>
            <span class="profile-card__rate-value">{{ computedMin }}–{{ computedMax }}</span
            >/hr
          </span>
        </div>
      </div>
      <div v-if="variant === SitterCardVariantTypes.Small" class="w-full">
        <div class="h-[120px]">
          <img
            class="h-[120px] absolute rounded-t-xl left-0 top-0 object-cover overflow-hidden w-full"
            :src="photoUrl"
            :alt="photoAltText"
            :aria-label="photoAltText"
          />
        </div>
        <div>
          <h3 class="text-h5">{{ sitterNameTruncated }}</h3>
          <p>{{ yearsExperiencePluralized }}</p>
          <p v-if="hasRate">${{ computedMin }}–{{ computedMax }}/hr</p>
          <StarRating v-if="totalRatings > 0" :count="totalRatings || 0" :average="avgRating" />
        </div>
      </div>
    </div>
  </Card>
</template>

<script lang="ts">
import { parseISO, format } from 'date-fns';
import helperStatus from '../../helpers/status';
import SitterCardVariantTypes from '../../types/sitter_card';

// Components
import AlertBanner from '../alert_banner/AlertBanner.vue';
import Avatar from '../avatar/Avatar.vue';
import Card from '../card/Card.vue';
import SitterCardLoadingSkeleton from './SitterCardLoadingSkeleton.vue';
import StarRating from '../star_rating/StarRating.vue';

// Badges & Icons
import IDVBadge from '../../graphics/badges/IDVBadge.vue';
import BackgroundCheckBadge from '../../graphics/badges/BackgroundCheckBadge.vue';
import CPRBadge from '../../graphics/badges/CPRBadge.vue';
import EnhancedBackgroundCheckBadge from '../../graphics/badges/EnhancedBackgroundCheckBadge.vue';
import FirstAidBadge from '../../graphics/badges/FirstAidBadge.vue';
import MotorVehicleRecordBadgeVue from '../../graphics/badges/MotorVehicleRecordBadge.vue';
import CalendarIcon from '../../icons/CalendarIcon.vue';
import AddFilled from '../../icons/AddFilled.vue';

const METERS_TO_MILES_CONVERSION = 1609.344;
const LIMIT_WORD_COUNT = 300;

export default {
  name: 'SitterCard',
  components: {
    AlertBanner,
    Avatar,
    Card,
    SitterCardLoadingSkeleton,
    StarRating,

    // Badges & Icons
    BackgroundCheckBadge,
    CPRBadge,
    EnhancedBackgroundCheckBadge,
    FirstAidBadge,
    IDVBadge,
    MotorVehicleRecordBadgeVue,
    CalendarIcon,
    AddFilled,
  },
  props: {
    availabilityChecks: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: Number as () => SitterCardVariantTypes,
      default: SitterCardVariantTypes.Base,
    },
    name: {
      type: String,
      default: '',
    },
    city: {
      type: String,
      default: '',
    },
    stateAbbr: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    avgRating: {
      type: Number,
      default: null,
    },
    minRate: {
      type: Number,
      default: null,
    },
    maxRate: {
      type: Number,
      default: null,
    },
    distance: {
      type: Number,
      default: null,
    },
    totalRatings: {
      type: Number || null,
      default: null,
    },
    photoUrl: {
      type: String,
      default: '',
    },
    yearsExperience: {
      type: Number || null,
      default: null,
    },
    hasBgc: {
      type: Boolean,
      default: false,
    },
    hasIdv: {
      type: Boolean,
      default: false,
    },
    hasEnhancedBgc: {
      type: Boolean,
      default: false,
    },
    hasMvr: {
      type: Boolean,
      default: false,
    },
    hasFirstAid: {
      type: Boolean,
      default: false,
    },
    hasCpr: {
      type: Boolean,
      default: false,
    },
    sitterIdForProfile: {
      type: String,
      required: false,
      default: null,
    },
    caretype: {
      type: String,
      required: false,
      default: 'babysitter',
    },
    availability: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    customClasses: {
      type: String,
      default: '',
      required: false,
    },
    photoUploadAvatar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      SitterCardVariantTypes,
    };
  },
  computed: {
    isClickable(): boolean {
      return !!this.sitterIdForProfile;
    },
    isRecruited(): boolean {
      return this.availabilityChecks?.includes(this.sitterIdForProfile);
    },
    shouldShowAvailabilityAlert(): boolean {
      return this.isAvailabilityRoute && this.isRecruited;
    },
    isAvailabilityRoute(): boolean {
      return this.$route?.path === '/availability/search';
    },
    yearsExperiencePluralized(): string {
      const experience = this.yearsExperience;
      if (experience === 1) {
        return `${experience} year experience`;
      }

      return `${experience} years experience`;
    },
    locationText(): string {
      let locationDistanceSeparater = '';
      let cityStateSeparater = '';
      if ((this.city || this.stateAbbr) && this.computedDistance) locationDistanceSeparater = ' • ';
      if (this.city && this.stateAbbr) cityStateSeparater = ', ';
      return `${this.city}${cityStateSeparater}${this.stateAbbr}${locationDistanceSeparater}${this.computedDistance}`;
    },
    computedDistance(): string {
      if (!this.distance) return '';
      const miles = Math.round(Number(this.distance) / METERS_TO_MILES_CONVERSION);
      if (miles < 1) return '< 1 mile';
      if (miles === 1) return '1 mile';
      return `${miles} miles`;
    },
    truncatedDescription(): string {
      if (!this.description) return '';
      /* eslint-disable no-useless-escape */
      const regex = new RegExp(`^(.{${LIMIT_WORD_COUNT}}[^\s]*).*`);
      /* eslint-enable no-useless-escape */
      const truncated = this.description.replace(/(\r\n|\n|\r)/gm, '').replace(regex, '$1');
      return `${truncated}...`;
    },
    computedMin(): number {
      // https://git.sittercity.com/sittercity/sittercity/blob/master/app/presenters/jobs/pay_rate_info.rb#L38
      return Math.floor(this.minRate / 100);
    },
    computedMax(): number {
      // https://git.sittercity.com/sittercity/sittercity/blob/master/app/presenters/jobs/pay_rate_info.rb#L39
      return Math.ceil(this.maxRate / 100);
    },
    hasRate(): boolean {
      return this.computedMin > 0 && this.computedMax > 0;
    },
    profileUrl(): string | boolean {
      if (!this.sitterIdForProfile) {
        return false;
      }
      return `/providers/${this.sitterIdForProfile}?care_type=${this.caretype}`;
    },
    photoAltText(): string {
      return `${this.sitterNameTruncated} ${this.yearsExperiencePluralized} ${this.computedMin}–${this.computedMax}/hr`;
    },
    sitterNameTruncated(): string {
      if (this.name.length > 20) {
        return `${this.name.substring(0, 20)}...`;
      }
      return this.name;
    },
    availabilityText(): string {
      const s = parseISO(this.availability.start);
      const e = parseISO(this.availability.end);
      return `${format(s, 'MMM d, h:mm aaa')} – ${format(e, 'h:mm aaa')}`;
    },
    validStatus(): helperStatus {
      return helperStatus.Valid;
    },
    hideBGCBadge(): boolean {
      return this.hasIdv && this.hasEnhancedBgc && this.hasBgc && this.hasMvr && this.hasFirstAid && this.hasCpr;
    },
  },
};
</script>

<style lang="scss" scoped>
.small-sitter-card {
  flex: 1 1;
  width: 0;
  padding-top: 0.5rem;
}
#sitter-card-description {
  word-wrap: break-word;
}
</style>
